import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import StyledModal from '../../../../components/modal/styledModals/StyledModal';
import SvgFragment from '../../../../fragments/SvgFragment';
import { styles } from '../../../../styles/themes/style';

/**
 * What someone typed as a link address, made safe to put in an email — or null when
 * it is not an address at all.
 *
 * Only http(s): anything else in an email is either inert or hostile, and the server
 * strips it on save anyway. A bare "example.com" gets https:// in front of it.
 */
export function normalizeLinkUrl(raw: string): string | null {
  const url = raw.trim();
  if (!url) return null;
  const safe = /^https?:\/\//i.test(url) ? url : `https://${url.replace(/^\/+/, '')}`;
  return /^https?:\/\/[^\s"'<>]+$/i.test(safe) ? safe : null;
}

interface Props {
  show: boolean;
  onHide: () => void;
  /** Receives the address already normalised — see normalizeLinkUrl. */
  onSubmit: (url: string) => void;
}

/**
 * The address for the editor's Link button, asked in our own dialog.
 *
 * It was window.prompt: the browser's grey box, titled with the site's address, which
 * reads as something other than the app asking — and cannot say what was wrong with
 * what was typed.
 *
 * Focus enforcement is off on purpose. The link is applied to the text selected in the
 * editor behind this dialog, which means handing focus back to that editor while the
 * dialog is still fading out — and an enforcing dialog pulls it straight back, so the
 * link went nowhere.
 */
const LinkUrlDialog: React.FC<Props> = ({ show, onHide, onSubmit }) => {
  const [value, setValue] = useState('');
  const [error, setError] = useState('');

  // A fresh field every time — the last address is not a useful default.
  useEffect(() => {
    if (!show) return;
    setValue('');
    setError('');
  }, [show]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const url = normalizeLinkUrl(value);
    if (!url) {
      setError('Enter a web address, like example.com.');
      return;
    }
    onSubmit(url);
  };

  return (
    <StyledModal show={show} onHide={onHide} width="420px" enforceFocus={false} restoreFocus={false}>
      <Body onSubmit={submit} noValidate>
        <Header>
          <Title>Insert link</Title>
          <CloseBtn type="button" onClick={onHide} aria-label="Close">
            <SvgFragment type="close" />
          </CloseBtn>
        </Header>

        <Field>
          <FieldLabel htmlFor="ld-link-url">Link URL</FieldLabel>
          <Input
            id="ld-link-url"
            autoFocus
            type="text"
            inputMode="url"
            placeholder="https://example.com"
            value={value}
            $invalid={!!error}
            onChange={e => { setValue(e.target.value); if (error) setError(''); }}
            // Password managers draw into bare inputs; see the email subject field.
            name="ld-link-url"
            autoComplete="off"
            data-lpignore="true"
            data-1p-ignore="true"
            data-form-type="other"
          />
          {error && <FieldError>{error}</FieldError>}
        </Field>

        <Footer>
          <SecondaryBtn type="button" onClick={onHide}>Cancel</SecondaryBtn>
          <PrimaryBtn type="submit">Insert link</PrimaryBtn>
        </Footer>
      </Body>
    </StyledModal>
  );
};

export default LinkUrlDialog;

// ─── Styles ───────────────────────────────────────────────────────────────────
// The same dialog language as AddLinkedInAccountModal.

const Body = styled.form`
  display: flex; flex-direction: column; gap: 14px;
  padding: 20px 24px 24px;
`;

const Header = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 16px;
`;

const Title = styled.h3`
  margin: 0;
  font-family: NunitoSans; font-size: 18px; font-weight: 800;
  color: ${styles.colors.black600};
`;

const CloseBtn = styled.button`
  background: none; border: none; padding: 0; cursor: pointer; line-height: 0;
  .svg { width: 20px; height: 20px; }
`;

const Field = styled.div`
  display: flex; flex-direction: column; gap: 5px;
`;

const FieldLabel = styled.label`
  font-family: NunitoSans; font-size: 12px; font-weight: 700;
  color: ${styles.colors.black500};
`;

const Input = styled.input<{ $invalid?: boolean }>`
  height: 40px; width: 100%; box-sizing: border-box;
  padding: 0 12px; border-radius: 8px; outline: none;
  border: 1.5px solid ${({ $invalid }) => ($invalid ? '#ef4444' : '#dde3ee')};
  background: ${styles.colors.white};
  font-family: NunitoSans; font-size: 13px; color: ${styles.colors.black600};
  &::placeholder { color: ${styles.colors.black300}; }
  &:focus {
    border-color: ${({ $invalid }) => ($invalid ? '#ef4444' : styles.colors.primary600)};
    box-shadow: 0 0 0 3px ${({ $invalid }) => ($invalid ? 'rgba(239,68,68,0.10)' : 'rgba(89,100,255,0.08)')};
  }
`;

const FieldError = styled.span`
  font-family: NunitoSans; font-size: 11.5px; font-weight: 600; color: #dc2626;
`;

const Footer = styled.div`
  display: flex; align-items: center; justify-content: flex-end; gap: 10px;
  margin-top: 6px;
`;

const SecondaryBtn = styled.button`
  height: 38px; padding: 0 16px; border-radius: 8px;
  border: 1px solid #dde3ee; background: ${styles.colors.white};
  font-family: NunitoSans; font-size: 13px; font-weight: 700;
  color: ${styles.colors.black500}; cursor: pointer;
  &:hover { background: #f7f9fc; }
`;

const PrimaryBtn = styled.button`
  height: 38px; padding: 0 18px; border-radius: 8px; border: none;
  background: ${styles.colors.primary600}; color: ${styles.colors.white};
  font-family: NunitoSans; font-size: 13px; font-weight: 700; cursor: pointer;
  &:hover { background: ${styles.colors.primary500}; }
`;
